import { CSSProperties } from "react";

/**
 * Slides are authored at a fixed 1280x720 and scaled to fit the editor, so
 * every number below is the literal Figma value. Editing at true design size
 * is what keeps the canvas honest - a slide cannot look different in the editor
 * than it does at runtime because it is the same geometry, only scaled.
 */
export const SLIDE_WIDTH = 1280;
export const SLIDE_HEIGHT = 720;

/**
 * The PowerSlide/* text styles. Letter spacing is stored as a percentage in
 * Figma; the px values here are that percentage resolved at each size.
 */
export const powerSlideType = {
  sectionTitle: {
    fontFamily: "Poppins, sans-serif",
    fontWeight: 700,
    fontSize: 80,
    lineHeight: 1.15,
    letterSpacing: "-1.2px",
  },
  title: {
    fontFamily: "Poppins, sans-serif",
    fontWeight: 700,
    fontSize: 64,
    lineHeight: 1.2,
    letterSpacing: "-0.64px",
  },
  slideTitle: {
    fontFamily: "Poppins, sans-serif",
    fontWeight: 700,
    fontSize: 44,
    lineHeight: 1.3,
    letterSpacing: "-0.22px",
  },
  subtitle: {
    fontFamily: "Poppins, sans-serif",
    fontWeight: 400,
    fontSize: 28,
    lineHeight: 1.4,
    letterSpacing: "0px",
  },
  body: {
    fontFamily: "Poppins, sans-serif",
    fontWeight: 400,
    fontSize: 24,
    lineHeight: 1.5,
    letterSpacing: "0px",
  },
  eyebrow: {
    fontFamily: "Poppins, sans-serif",
    fontWeight: 600,
    fontSize: 18,
    lineHeight: 1.2,
    letterSpacing: "2.7px",
  },
  listNumber: {
    fontFamily: "Poppins, sans-serif",
    fontWeight: 600,
    fontSize: 16,
    lineHeight: 1,
    letterSpacing: "0px",
  },
  statNumber: {
    fontFamily: "Poppins, sans-serif",
    fontWeight: 600,
    fontSize: 80,
    lineHeight: 1.1,
    letterSpacing: "-1.6px",
  },
  caption: {
    fontFamily: "Poppins, sans-serif",
    fontWeight: 400,
    fontSize: 18,
    lineHeight: 1.45,
    letterSpacing: "0px",
  },
  /** In-slide chrome: upload prompts and the video duration badge. */
  uiLabel: {
    fontFamily: "Poppins, sans-serif",
    fontWeight: 500,
    fontSize: 16,
    lineHeight: 1.2,
    letterSpacing: "0px",
  },
} satisfies Record<string, CSSProperties>;

export type PowerSlideTypeStyle = keyof typeof powerSlideType;

/** Figma's radius/* variables, as used by the slide blocks. */
export const powerSlideRadius = {
  sm: 6,
  lg: 8,
  pill: 999,
};

/**
 * Padding on the editable block that wraps each text region. It is identical in
 * Runtime and Editing so text never shifts when a field gains its affordance -
 * which is also why the title's left edge is a constant 120px deck-wide
 * (104 slide padding + 16 block padding).
 */
export const powerSlideBlockPadding = {
  /** Title, body, eyebrow and subtitle blocks on left-aligned templates. */
  standard: { paddingInline: 16, paddingBlock: 12 },
  /** Title and subtitle blocks on the centred templates. */
  centred: { paddingInline: 24, paddingBlock: 12 },
  /** List rows. */
  listItem: { paddingInline: 12, paddingBlock: 8 },
  /** By the Numbers title, values and labels. */
  compact: { paddingInline: 16, paddingBlock: 8 },
  stat: { paddingInline: 12, paddingBlock: 8 },
};

/** Slide padding, which differs between the left-aligned and centred families. */
export const powerSlidePadding = {
  left: { paddingInline: 104, paddingBlock: 64 },
  centre: { paddingInline: 120, paddingBlock: 64 },
};

export const powerSlideMetrics = {
  /** Gap between the header and the body on Basic, Key Points and Numbered. */
  contentGap: 36,
  /** Title-to-divider gap on Basic. */
  basicHeaderGap: 20,
  /** Title-to-accent-rule gap on Key Points and Numbered. */
  listHeaderGap: 12,
  /** Full-width 2px rule under the Basic title. */
  dividerHeight: 2,
  /** Short accent rule under the Key Points and Numbered titles. */
  accentWidth: 220,
  accentHeight: 4,
  listGap: 8,
  listRowGap: 10,
  /** Bullet dot and number badge. */
  dotSize: 10,
  badgeSize: 28,
  /** Section slide's eyebrow rules. */
  eyebrowRuleWidth: 128,
  eyebrowRowGap: 16,
  titleContentGap: 24,
  sectionContentGap: 16,
  statsTitleGap: 28,
  statsRowGap: 24,
  statColumnGap: 4,
};

/** The number badge chip on Numbered slides. */
export const POWER_SLIDE_BADGE_FILL = "#daf2ff";

/** Media templates: the asset area, its grid, and the in-slide controls. */
export const powerSlideMedia = {
  /** Gap between the title, the media area and the caption. */
  stackGap: 28,
  /** Gap between slots, and between the rows of the Gallery grid. */
  gridGap: 24,
  /** Gap between a slot and its own caption. */
  slotCaptionGap: 12,
  /** Empty-slot prompt: glyph over label. */
  promptGap: 12,
  glyphWidth: 48,
  glyphHeight: 40,
  /** Dashed accent border that marks an empty asset slot. */
  emptyBorderWidth: 2,
  /** Hover controls pinned inside the slot's top-right corner. */
  controlInset: 16,
  controlGap: 8,
  controlSize: 32,
  controlIconSize: 16,
  /** Video play button and duration badge. */
  playButtonSize: 76,
  playGlyphWidth: 22,
  playGlyphHeight: 26,
  durationInset: 16,
  durationPaddingInline: 10,
  durationPaddingBlock: 4,
};

/** Overlay Image ignores slide padding: the artwork is full bleed. */
export const powerSlideOverlay = {
  scrimHeight: 380,
  textLeft: 52,
  textBottom: 56,
  textWidth: 760,
  textGap: 6,
  blockPaddingInline: 12,
  blockPaddingBlock: 8,
  scrimFrom: "rgba(28, 41, 56, 0)",
  scrimTo: "rgba(28, 41, 56, 0.85)",
};
